import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  TemplateRef,
} from '@angular/core';
import { DisplayGrid, GridsterConfig, GridsterItem, GridType } from 'angular-gridster2';

export interface DashboardWidget extends GridsterItem {
  id: string | number;
  componentType?: string;
  componentData?: any;
  hasContent?: boolean;
  label?: string;
  [key: string]: any;
}

@Component({
  selector: 'app-view-gridster-dashboard',
  templateUrl: './view-gridster-dashboard.component.html',
  styleUrl: './view-gridster-dashboard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ViewGridsterDashboardComponent implements OnInit, OnChanges {
  @Input() widgets: DashboardWidget[] = [];
  @Input() options?: Partial<GridsterConfig>;
  @Input() showAddButton: boolean = false;
  @Input() showSaveButton: boolean = false;
  /** When true, the save-layout control shows loading and is non-interactive. */
  @Input() saveLayoutBusy: boolean = false;
  @Input() showRemoveButton: boolean = true;
  @Input() showEditButton: boolean = false;
  @Input() showDragHandle: boolean = true;
  /** Optional per-widget content; rendered with the widget as `$implicit` context. */
  @Input() contentTemplate?: TemplateRef<{ $implicit: DashboardWidget }>;

  // Helper method برای دسترسی به widget در template
  getWidgetById(id: string | number): DashboardWidget | undefined {
    return this.dashboard.find((w) => w.id === id);
  }
  @Output() widgetChange = new EventEmitter<DashboardWidget[]>();
  @Output() widgetAdded = new EventEmitter<DashboardWidget>();
  @Output() widgetRemoved = new EventEmitter<DashboardWidget>();
  @Output() widgetEdit = new EventEmitter<DashboardWidget>();
  @Output() widgetSaved = new EventEmitter<DashboardWidget[]>();
  @Output() widgetDragged = new EventEmitter<{
    item: DashboardWidget;
    event: MouseEvent;
  }>();
  @Output() widgetResized = new EventEmitter<{
    item: DashboardWidget;
    event: MouseEvent;
  }>();

  dashboard: DashboardWidget[] = [];
  gridsterOptions: GridsterConfig = {
    // Fixed row height + grow-with-content so tiles keep their size and the
    // dashboard scrolls vertically as more widgets are added (Fit would stretch
    // a single widget to fill the whole area and never scroll).
    gridType: GridType.VerticalFixed,
    fixedRowHeight: 110,
    setGridSize: true,
    displayGrid: DisplayGrid.OnDragAndResize,
    margin: 12,
    minCols: 12,
    maxCols: 12,
    minRows: 1,
    pushItems: true,
    swap: false,
    draggable: {
      delayStart: 0,
      enabled: true,
      ignoreContentClass: 'gridster-item-content',
      ignoreContent: true, // فقط با drag-handler می‌شود drag کرد
      dragHandleClass: 'drag-handler',
      stop: (item: any, itemComponent: any, event: MouseEvent) =>
        this.onDragStop(item, itemComponent, event),
      start: (item: any, itemComponent: any, event: MouseEvent) =>
        this.onDragStart(item, itemComponent, event),
      dropOverItems: false,
      dropOverItemsCallback: (source: any, target: any, grid: any) =>
        this.onDropOver(source, target, grid),
    },
    resizable: {
      enabled: true,
      stop: (item: any, itemComponent: any, event: MouseEvent) =>
        this.onResizeStop(item, itemComponent, event),
      start: (item: any, itemComponent: any, event: MouseEvent) =>
        this.onResizeStart(item, itemComponent, event),
    },
  };

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.initializeDashboard();
    this.mergeCustomOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['widgets'] && !changes['widgets'].firstChange) {
      this.initializeDashboard();
      this.cdr.detectChanges();
      // After items change (e.g. a widget added on save), nudge gridster to
      // recompute its layout and size so the new tile renders in place.
      setTimeout(() => this.gridsterOptions.api?.optionsChanged?.());
    }
    if (changes['options'] && !changes['options'].firstChange) {
      this.mergeCustomOptions();
      this.changedOptions();
    }
  }

  private initializeDashboard(): void {
    const next = this.widgets.map((widget) => ({ ...widget }));
    // Mutate the same array reference — angular-gridster2 tracks the items array
    // by reference, so reassigning it can leave newly added items unrendered.
    this.dashboard.splice(0, this.dashboard.length, ...next);
  }

  private mergeCustomOptions(): void {
    if (this.options) {
      this.gridsterOptions = {
        ...this.gridsterOptions,
        ...this.options,
        draggable: {
          ...this.gridsterOptions.draggable,
          ...this.options.draggable,
        },
        resizable: {
          ...this.gridsterOptions.resizable,
          ...this.options.resizable,
        },
      };
    }
  }

  changedOptions(): void {
    this.gridsterOptions = { ...this.gridsterOptions };
    this.cdr.detectChanges();
  }

  onDragStart(item: any, itemComponent: any, event: MouseEvent): void {
    const widget = this.dashboard.find((w) => w.id === (item as DashboardWidget).id);
    if (widget) {
      this.widgetDragged.emit({ item: widget, event });
    }
  }

  onDragStop(item: any, itemComponent: any, event: MouseEvent): void {
    this.widgetChange.emit([...this.dashboard]);
    this.cdr.detectChanges();
  }

  onDropOver(source: any, target: any, grid: any): void {
    // Handle drop over logic if needed
  }

  onResizeStart(item: any, itemComponent: any, event: MouseEvent): void {
    // Handle resize start if needed
  }

  onResizeStop(item: any, itemComponent: any, event: MouseEvent): void {
    const widget = this.dashboard.find((w) => w.id === (item as DashboardWidget).id);
    if (widget) {
      this.widgetResized.emit({ item: widget, event });
      this.widgetChange.emit([...this.dashboard]);
    }
    this.cdr.detectChanges();
  }

  editItem(event: MouseEvent | TouchEvent, item: DashboardWidget): void {
    event.preventDefault();
    event.stopPropagation();
    this.widgetEdit.emit(item);
  }

  removeItem(event: MouseEvent | TouchEvent, item: DashboardWidget): void {
    event.preventDefault();
    event.stopPropagation();
    const index = this.dashboard.indexOf(item);
    if (index > -1) {
      const removedWidget = this.dashboard.splice(index, 1)[0];
      this.widgetRemoved.emit(removedWidget);
      this.widgetChange.emit([...this.dashboard]);
      this.cdr.detectChanges();
    }
  }

  addItem(): void {
    const newId =
      this.dashboard.length > 0
        ? Math.max(...this.dashboard.map((w) => (typeof w.id === 'number' ? w.id : 0))) + 1
        : 1;

    const newWidget: DashboardWidget = {
      x: 0,
      y: 0,
      cols: 1,
      rows: 1,
      id: newId,
      hasContent: true,
    };

    // Emit to parent so it can open the wizard — don't push to grid yet.
    // The widget will be added to the grid after the wizard completes
    // and the parent reloads widgets from the API.
    this.widgetAdded.emit(newWidget);
  }

  trackByFn(index: number, item: DashboardWidget): string | number {
    return item.id;
  }

  saveWidgets(): void {
    // Emit the current dashboard state for saving
    this.widgetSaved.emit([...this.dashboard]);
  }
}
